// CR.tsx
import { useState, useEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import { RequestChnages } from '../../../../assets/common-svg/CommonSvg';
import { useSelector } from "react-redux";
import { selectCurrentCycle } from "../../../slices/common"
import { selectedSubjectData } from "../../../slices/help"
import { hasAuthority } from '../../../../utils/authorities'
import bmrSubjectApi from '../../../../bmr/services/bmrSubjectService';
import SubjectApi from "../../../../services/subjectService";
import CustomModel from "../../../../../src/components/custom-model/CustomModel"
import { requestChangeInitial } from '../../../../utils/formik/initialValues';
import { requestModalDataSchema } from '../../../../utils/formik/validationSchema';
import { notify } from '../../../utils/methods/notification';
import { roleBasedExclusions } from '../../../utils/common/utils'
import { setSubjectDataList } from "../../../../bmr/slices/help";
import { useDispatch } from "react-redux";
import { filterResultFeasibility, hasCRPermission } from '../../../../utils/utils';

const CR = ({
  actualSteps = [],
  cr_type = 2,
  subject_id = '',
  feasibility_id = '',
  phase_id = '',
  onUpdate,
  show = '',
}) => {
  const LoginData = useSelector((state) => state.loginData);
  const bmr_permissions = LoginData?.bmr_permissions || [];
  const cr_permissions = LoginData?.cr_permissions || [];
  const SubjectData = useSelector(selectedSubjectData);
  const role_slug = LoginData?.role_slug;
  const [reasonsList, setReasonsList] = useState([]);
  const currentCycle = useSelector(selectCurrentCycle)
  const [isRequestChangeModal, setIsRequestChangeModal] = useState(false);
  const [requestModalData, setRequestModalData] = useState({
    title: "Request To Update",
    children: [],
  });

  const clinicalPermissionStatus = (
    hasAuthority(["marked-clinical-feasible"], bmr_permissions) || hasCRPermission(cr_permissions, ["change-clinical-feasibility"], "RAISE")) &&
    SubjectData?.feasibility_assessments?.clinical_status !== 0;
  const financialPermissionStatus =
    (hasAuthority(["marked-financial-feasible"], bmr_permissions) || hasCRPermission(cr_permissions, ["change-financial-feasibility"], "RAISE")) &&
    SubjectData?.feasibility_assessments?.financial_status != 0;
  const [recordCurrentInfo, setRecordCurrentInfo] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const role_id = LoginData?.role_id;
  const isCommercial = SubjectData?.phase_details?.phase_type?.name?.toLowerCase() === "commercial";
  const phaseLabels = {
    patient: isCommercial ? "Patient " : "Subject ",
    hospital: isCommercial ? "Hospital " : "Site ",
    investigator: isCommercial ? "HCP" : "Principal Investigator",
  };
  const dispatch = useDispatch();

  const filterReasonDropdown = useCallback((finalResult, subjectD = SubjectData) => {
    const isSubjectAlive = subjectD?.life_status === "alive";

    const isCycleTerminated = currentCycle?.status === 2;
    const filter1 = cr_type == 2
      ? isCycleTerminated ? ['survival-status'] : ['survival-status', 'change-terminate-cycle-status']
      : ['change-terminate-cycle-status', 'survival-status']

    const result = isSubjectAlive
      ? finalResult?.data
        ?.filter((item) => !item?.slug?.includes(filter1))
        .filter((item) =>
          hasCRPermission(cr_permissions, [item.slug], "RAISE") ||
          hasCRPermission(cr_permissions, item?.children?.map((it) => it?.slug), "RAISE")
        )
        .map((item) => ({
          id: item?.id,
          name: item?.title,
          slug: item?.slug,
        }))
      : finalResult?.data
        ?.filter((item) => item?.slug == 'survival-status')
        ?.filter((item) =>
          hasCRPermission(cr_permissions, [item.slug], "RAISE") ||
          hasCRPermission(cr_permissions, item?.children?.map((it) => it?.slug), "RAISE")
        )
        ?.map((item) => ({
          id: item?.id,
          name: item?.title,
          slug: item?.slug,
        }));
    setReasonsList(result);
  }, [SubjectData, cr_permissions, cr_type]);

  /**
   * Returns the visible children for a given parent reason item.
   * This mirrors the filtering logic inside handleStatusChange so we can
   * pre-check whether a parent has at least one visible child before
   * showing it in the first dropdown.
   */
  const getVisibleChildren = useCallback((reasonInfoDetail) => {
    if (!reasonInfoDetail) return [];

    let filteredChildren = [...(reasonInfoDetail.children || [])];

    if (reasonInfoDetail.slug === "feasibility-assessment") {
      const apheresisStep = actualSteps?.find(step => step.name === "Apheresis Collection");
      const isApheresisPending =
        !apheresisStep || (apheresisStep.status !== 2 && apheresisStep.status !== 1);

      filteredChildren = filteredChildren?.filter((item) => {
        if (
          hasCRPermission(cr_permissions, ["change-scheduled-aphresis-date-range"], "RAISE") &&
          item.slug === "change-scheduled-aphresis-date-range" &&
          SubjectData?.feasibility_assessments &&
          !SubjectData?.feasibility_assessments?.apheresis_date
        ) {
          return true;
        }
        if (
          hasCRPermission(cr_permissions, ["change-assigned-apheresis-date"], "RAISE") &&
          item.slug === "change-assigned-apheresis-date" &&
          SubjectData?.feasibility_assessments?.requested_date &&
          ((SubjectData?.feasibility_assessments?.requested_date && actualSteps.length == 0) ||
            (isApheresisPending && actualSteps?.length != 0) || SubjectData?.feasibilityAssessments[0]?.apheresis_timeline_steps?.length == 0)
        ) {
          return true;
        }

        if (
          hasCRPermission(cr_permissions, ["change-terminate-cycle-status"], "RAISE") &&
          item.slug === "change-terminate-cycle-status" &&
          currentCycle?.status === 2
        ) {
          return true;
        }

        if (
          clinicalPermissionStatus &&
          item.slug === "change-clinical-feasibility" &&
          SubjectData?.feasibility_assessments &&
          !SubjectData?.patient_slots?.slot?.date
        ) {
          return true;
        }

        if (
          financialPermissionStatus &&
          item.slug === "change-financial-feasibility" &&
          SubjectData?.feasibility_assessments &&
          !SubjectData?.patient_slots?.slot?.date
        ) {
          return true;
        }

        return false;
      });
    }

    if (reasonInfoDetail.slug === "infusion") {
      const infusionStep = actualSteps?.find(step => step.slug === "infusion");
      const isCompleteInfusionDateAdded = infusionStep && (infusionStep.completed_date != null || infusionStep.status === 2);

      // Only show "change-complete-infusion" — hide ALL other children unconditionally
      filteredChildren = filteredChildren?.filter((item) => {
        if (item.slug === "change-complete-infusion" || item.slug === "complete-infusion-date") {
          return isCompleteInfusionDateAdded && hasCRPermission(cr_permissions, ["change-complete-infusion", "complete-infusion-date"], "RAISE");
        }
        // All other infusion children are hidden
        return false;
      });
    }

    return filteredChildren;
  }, [
    actualSteps,
    cr_permissions,
    cr_type,
    SubjectData,
    currentCycle,
    clinicalPermissionStatus,
    financialPermissionStatus,
  ]);

  /**
   * For parents whose slug is "feasibility-assessment" we must check visible
   * children via getVisibleChildren. For all other slugs the children list is
   * not filtered by handleStatusChange, so we only need to confirm the item
   * exists in recordCurrentInfo to know it has valid children.
   */
  const parentHasVisibleChildren = useCallback((item) => {
    const reasonInfoDetail = recordCurrentInfo?.data?.find((r) => r.id === item.id);
    if (!reasonInfoDetail) return false;

    if (reasonInfoDetail.slug === "feasibility-assessment" || reasonInfoDetail.slug === "infusion") {
      return getVisibleChildren(reasonInfoDetail).length > 0;
    }
    // For other parents, children are shown as-is — keep parent if it has any children
    return (reasonInfoDetail.children?.length ?? 0) > 0;
  }, [recordCurrentInfo, getVisibleChildren, cr_type]);

  // Handle Status Change in Request Modal
  const handleStatusChange = (selectedValue) => {
    const reasonInfoDetail = recordCurrentInfo.data?.find(
      (item) => item.id === selectedValue
    );

    if (!reasonInfoDetail) return;

    const filteredChildren = getVisibleChildren(reasonInfoDetail);
    const updatedMenu = filteredChildren?.map((item) => ({
      id: item.id,
      name: item.title,
    }));

    const normalizedReasonTitle = reasonInfoDetail?.title?.toLowerCase?.() || "";
    const isKitDetail = reasonInfoDetail.title === "Kit Dispatched Detail Update";
    const isLSMDetail = reasonInfoDetail.title === "LSM Detail Update";
    const isSlotDetail = reasonInfoDetail.title === "Slot Details Update";
    const isFinalProductDetail =
      reasonInfoDetail?.slug === "product-shipped" ||
      normalizedReasonTitle.includes("final product dispatched");

    setRequestModalData((prevState) => {
      const updatedChildren = prevState.children
        .filter((child) => child.name !== "comment")
        .map((child) => {
          if (child.name === "Select_patient_Info_menu") {
            return {
              ...child,
              data: updatedMenu,
              type:
                isSlotDetail || isLSMDetail || isKitDetail || isFinalProductDetail
                  ? "multidropdown"
                  : "dropdown",
              label: isLSMDetail
                ? "Select Fields that are required to changes"
                : `Select ${phaseLabels.patient}Info Reason`,
              placeholder: isLSMDetail
                ? "Select Fields"
                : `Select ${phaseLabels.patient}Info Reason`,
            };
          }
          return child;
        });

      return {
        ...prevState,
        children: updatedChildren,
      };
    });
  };

  const getSubjectDetails = async () => {
    try {
      const currentId = subject_id || currentCycle?.subject_id;
      let sub = SubjectData;
      if (!SubjectData || Object.keys(SubjectData)?.length < 1 || SubjectData.id != currentId) {
        const res = await SubjectApi.getSubjectDetails(currentId);
        sub = res?.data;
        await dispatch(setSubjectDataList(sub));
      }
      fetchData(sub);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = async (sub) => {
    try {
      const phaseTypeName = sub?.phase_details?.phase_type?.name;
      const res = await bmrSubjectApi.getAllRequest(phaseTypeName);
      if (res && res.data && Array.isArray(res.data)) {
        const infusionReason = res.data.find(item => item.slug === "infusion");
        if (infusionReason) {
          if (!infusionReason.children) {
            infusionReason.children = [];
          }
          const hasCompleteInfusion = infusionReason.children.some(
            child => child.slug === "complete-infusion-date" || child.slug === "complete-infusion-date"
          );
          if (!hasCompleteInfusion) {
            infusionReason.children.push({
              id: "complete-infusion-date",
              title: "Change Complete Infusion",
              name: "Change Complete Infusion",
              slug: "complete-infusion-date"
            });
          }
        }
      }
      setRecordCurrentInfo(res);
      filterReasonDropdown(res, sub);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const currentId = subject_id || currentCycle?.subject_id;
    if (currentId) {
      if (SubjectData && Object.keys(SubjectData)?.length > 0 && SubjectData.id == currentId) {
        fetchData(SubjectData);
      } else {
        getSubjectDetails();
      }
    }
  }, [subject_id, currentCycle?.subject_id, SubjectData?.id]);

  const handleOpenRequestModal = useCallback(async () => {
    let filteredReasonMenu = reasonsList;
    const excludedItems = roleBasedExclusions?.[cr_type] || [];
    const shouldAddSlotDetailsUpdate = (data) => {
      const QrDispatchedStep = actualSteps.find(step => step.name === "Label & Kit Preparation");
      if (data?.some(item => item.slug === "slot-details-update") && show != 'Request') {
        // Hide if Label & Kit Preparation step is completed (status === 2)
        if (QrDispatchedStep?.status === 2) {
          return true; // triggers filter that removes slot-details-update
        }

        // Hide if apheresis_date is not present
        if (!SubjectData?.feasibility_assessments?.apheresis_date) {
          return true;
        }
        if (
          filterResultFeasibility(
            feasibility_id || SubjectData?.feasibility_assessments?.id,
            SubjectData?.feasibilityAssessments,
            'timeline_steps'
          )
        ) {
          return true;
        }
        return false;
      }
      return true;
    };


    let filteredData = [];

    if (actualSteps && actualSteps?.length > 0 && cr_type == 2) {
      const feasibilityStep = actualSteps.find(step => step.slug === "feasibility-assessment");
      const QrDispatchedStep = actualSteps.find(step => step.name === "Label & Kit Preparation");
      const kitDispatchedStep = actualSteps.find(step => step.name === "Kit Dispatched to Hospital");
      const finalProductDispatchedStep = actualSteps.find(step => step.slug === 'product-shipped');
      const lsmDispatchedStep = actualSteps.find(step => step.name === "LSM Dispatched");
      const infusionStep = actualSteps.find(step => step.name === "Infusion Administered");
      const lsmDispatchedStop = actualSteps.find(step => step.slug === "lsm-received-bmr");
      const stopInfusion = actualSteps.find(step => step.name === "Final Product Dispatched");
      const lsmAphesisStep = actualSteps.find(step => step.name === "Apheresis Collection");
      const stopLSMDispatch = actualSteps.find(step => step.name === "LSM Qualified");
      const productReceivedbyHospitalStep = actualSteps.find(step => step.slug === "product-received");
      const productPublishedStep = actualSteps.find(step => step.slug === "qc-testing");
      const qareviewStep = actualSteps.find(step => step.slug === "qa-review");

      const isApheresisPending =
        !lsmAphesisStep || (lsmAphesisStep.status !== 2 && lsmAphesisStep.status !== 1);

      const itemsToFilter = [
        { step: feasibilityStep, condition: (step) => step?.status == 2 },
        { step: QrDispatchedStep, condition: (step) => step?.completed_date != null && stopInfusion?.status != 2 },
        { step: kitDispatchedStep, condition: (step) => step?.completed_date != null && lsmAphesisStep?.status < 1 && lsmAphesisStep?.lsm_received_cdm_status != 2 },
        { step: lsmDispatchedStep, condition: (step) => step?.completed_date != null && lsmDispatchedStop?.status != 2 && stopLSMDispatch?.lsm_received_bmr_status != 2 },
        { step: infusionStep, condition: (step) => (step?.infusion_date != null && stopInfusion?.status != 2) || (step?.completed_date != null || step?.status === 2) },
        { step: finalProductDispatchedStep, condition: (step) => step?.status == 2 && productReceivedbyHospitalStep?.status != 2 },
        { step: productPublishedStep, condition: (step) => step?.product_completed_date != null && qareviewStep?.status != 2 },
      ]
        .filter(({ step, condition }) => step && condition(step))
        .map(({ step }) => step.slug);

      filteredData = filteredReasonMenu.filter(item => itemsToFilter?.includes(item.slug));
      const feasibilityAlreadyIncluded = filteredData.some(
        item => item.slug === "feasibility-assessment"
      );
      if (
        !feasibilityAlreadyIncluded &&
        isApheresisPending &&
        SubjectData?.feasibility_assessments?.requested_date &&
        // SubjectData?.feasibilityAssessments[0]?.apheresis_timeline_steps?.length == 0 &&
        hasCRPermission(cr_permissions, ["change-assigned-apheresis-date"], "RAISE")
      ) {
        const feasibilityOption = filteredReasonMenu.find(
          item => item.slug === "feasibility-assessment"
        );
        if (feasibilityOption) {
          filteredData = [...filteredData, feasibilityOption];
        }
      }

    } else if (cr_type == 1) {
      filteredReasonMenu = filteredReasonMenu?.filter(
        item => !excludedItems.includes(item.name)
      );

      if (shouldAddSlotDetailsUpdate(filteredReasonMenu)) {
        filteredReasonMenu = filteredReasonMenu?.filter(
          item => item.slug !== "slot-details-update"
        );
      }
      filteredData = filteredReasonMenu;
    }

    // ── NEW: Remove any parent whose children are all hidden after filtering ──
    filteredData = filteredData.filter((item) => parentHasVisibleChildren(item));
    // ─────────────────────────────────────────────────────────────────────────

    setIsRequestChangeModal(true);
    setRequestModalData((prev) => ({
      ...prev,
      title: "Request To Update",
      children: [
        {
          type: "dropdown",
          name: "Select_patient_request_status",
          label: `Select ${phaseLabels.patient} Request Reason`,
          placeholder: `Select ${phaseLabels.patient}'s Request Status`,
          IsRequired: true,
          data: filteredData,
        },
        {
          type: "dropdown",
          name: "Select_patient_Info_menu",
          label: `Select ${phaseLabels.patient}'s Info Reason`,
          placeholder: `Select ${phaseLabels.patient}'s Info Reason`,
          IsRequired: true,
          data: [],
        },
        {
          type: "description",
          name: "description",
          label: "Reason For Change",
          placeholder: "Reason For Change",
          IsRequired: true,
        },
      ],
    }));
  }, [
    reasonsList,
    actualSteps,
    role_slug,
    cr_type,
    role_id,
    SubjectData,
    clinicalPermissionStatus,
    financialPermissionStatus,
    cr_permissions,
    feasibility_id,
    show,
    parentHasVisibleChildren,
  ]);

  const findActualIds = async (ids) => {
    if (!ids) return;
    const finalResponse = reasonsList?.find((it) => it?.id == ids);
    if (finalResponse?.slug === "feasibility-assessment") {
      const fallbackStep =
        actualSteps?.find((step) => step?.slug === "product-manufactured") ||
        actualSteps?.find((step) => step?.slug !== "feasibility-assessment");
      return fallbackStep?.id;
    }
    const kitIdList = actualSteps?.find(step => step.slug === finalResponse?.slug);
    return kitIdList?.id;
  };

  const handleRequestChangeSubmit = async (data) => {
    setButtonLoading(true);
    try {
      let convertPatientInfo = data.Select_patient_Info_menu.toString();
      const payload = {
        change_request_reason_id: data.Select_patient_request_status,
        change_request_reason_value_id: convertPatientInfo,
        description: data.description,
        subject_id: SubjectData.id || subject_id,
        phase_id: SubjectData.phase_id || phase_id,
        feasibility_assessments_id: currentCycle?.feasibility_assessments_id || feasibility_id || SubjectData?.feasibility_assessments?.id,
        cr_type: cr_type,
        product_manufacturing_stage_id: currentCycle?.product_manufacturing_id,
        qr_code_id: currentCycle?.qr_code_generation_id,
        step_type_id: await findActualIds(
          data?.Select_patient_request_status,
          data?.Select_patient_Info_menu
        ),
      };
      const res = await bmrSubjectApi.requestChange(payload);
      if (res.status === 200) {
        notify.success(res?.message);
        if (onUpdate && typeof onUpdate === 'function') onUpdate();
        setIsRequestChangeModal(false);
      }
    } catch (error) {
      notify.error(error?.[0]?.description || "Failed to submit request");
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        title={'Request Changes'}
        onClick={() => handleOpenRequestModal()}
      >
        <button style={{ cursor: "pointer" }}>
          <RequestChnages />
        </button>
      </Tooltip>

      {/* Request Changes Modal */}
      <CustomModel
        modelData={requestModalData}
        open={isRequestChangeModal}
        setOpen={setIsRequestChangeModal}
        modalInitialValues={requestChangeInitial}
        initialValuesValidationSchema={requestModalDataSchema}
        handleModelValues={handleRequestChangeSubmit}
        onChange={(e) => handleStatusChange(e)}
        isLoadingBtn={buttonLoading}
        resistOverFlow={false}
      />
    </>
  );
};

export default CR;